import type { ComponentProps, Ref } from "preact";
import { ICONS, type IconName } from "./icon-paths.ts";

/** One drawing. The filled one (when it has one) shows while its button is on. */
export function Icon({ name, solid = false }: { name: IconName; solid?: boolean }) {
  const drawing = ICONS[name];
  if (!drawing) return null;
  const filled = solid && drawing.solid !== undefined;
  return (
    <svg
      class={filled ? "icon solid" : "icon"}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      // The markup is this app's own drawing, never user text.
      dangerouslySetInnerHTML={{ __html: filled ? drawing.solid! : drawing.outline }}
    />
  );
}

interface IconButtonProps extends Omit<ComponentProps<"button">, "icon" | "label" | "ref"> {
  icon: IconName;
  /** The accessible name. */
  label: string;
  /** What the tooltip says; defaults to the label. */
  tip?: string;
  /** Draws the filled icon. */
  on?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
}

/** A round button with an icon and a tooltip: the name, and the shortcut where there is one. */
export function IconButton({
  icon,
  label,
  tip,
  on = false,
  buttonRef,
  class: extra,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type="button"
      class={extra ? `island-button ${extra}` : "island-button"}
      ref={buttonRef}
      aria-label={label}
      data-tip={tip ?? label}
      {...rest}
    >
      <Icon name={icon} solid={on} />
    </button>
  );
}
